import type { CountryCode } from 'libphonenumber-js';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { sortedCountries } from '@/lib/countries';
import {
  DEFAULT_COUNTRY,
  formatNationalPhoneInput,
  getPhoneExampleForCountry,
  isValidPhoneForCountry,
  nationalDisplayFromE164,
  normalizePhoneForCountry,
} from '@/lib/phone';

export interface GuestPhoneFieldProps {
  value: string | null;
  country: CountryCode;
  onChange: (value: string | null) => void;
  onCountryChange: (country: CountryCode) => void;
  disabled?: boolean;
}

export const GuestPhoneField = ({
  value,
  country,
  onChange,
  onCountryChange,
  disabled = false,
}: GuestPhoneFieldProps) => {
  const { i18n, t } = useTranslation();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [national, setNational] = useState(() => (value ? nationalDisplayFromE164(value) : ''));
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [blurred, setBlurred] = useState(false);

  const countries = useMemo(() => sortedCountries(i18n.language), [i18n.language]);
  const selected = countries.find((entry) => entry.code === country) ?? countries[0];
  const example = useMemo(() => getPhoneExampleForCountry(country), [country]);
  const draftValid = !national.trim() || isValidPhoneForCountry(national, country);
  const showInvalid = blurred && !draftValid;

  const filteredCountries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return countries;
    return countries.filter(
      (entry) =>
        entry.name.toLowerCase().includes(query) ||
        entry.dial.includes(query) ||
        entry.code.toLowerCase().includes(query),
    );
  }, [countries, search]);

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [menuOpen]);

  const emit = (nextCountry: CountryCode, nextNational: string) => {
    if (!nextNational.trim()) {
      onChange(null);
      return;
    }
    onChange(normalizePhoneForCountry(nextNational, nextCountry));
  };

  return (
    <div className="checkin-field" ref={rootRef}>
      <span id={`${listId}-label`}>{t('guest.phone')}</span>
      <div className={`checkin-phone-row${showInvalid ? ' checkin-phone-row--invalid' : ''}`}>
        <div className="checkin-phone-country">
          <button
            type="button"
            className="checkin-phone-country-trigger"
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            aria-labelledby={`${listId}-label ${listId}-country`}
            disabled={disabled}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span id={`${listId}-country`} className="checkin-phone-country-value">
              <span aria-hidden>{selected.flag}</span>
              <span>{selected.dial}</span>
            </span>
          </button>

          {menuOpen ? (
            <div className="checkin-phone-country-menu" role="listbox" aria-label={t('guest.phoneCountry')}>
              <input
                type="search"
                className="checkin-phone-country-search"
                value={search}
                placeholder={t('guest.phoneCountrySearch')}
                onChange={(event) => setSearch(event.target.value)}
              />
              <ul className="checkin-phone-country-list">
                {filteredCountries.map((entry) => (
                  <li key={entry.code}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={entry.code === country}
                      className="checkin-phone-country-option"
                      onClick={() => {
                        onCountryChange(entry.code);
                        const reformatted = formatNationalPhoneInput(national, entry.code);
                        setNational(reformatted);
                        emit(entry.code, reformatted);
                        setMenuOpen(false);
                        setSearch('');
                      }}
                    >
                      <span aria-hidden>{entry.flag}</span>
                      <span className="checkin-phone-country-option-name">{entry.name}</span>
                      <span className="checkin-phone-country-option-dial">{entry.dial}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <input
          required
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          disabled={disabled}
          value={national}
          placeholder={example}
          aria-invalid={showInvalid || undefined}
          aria-labelledby={`${listId}-label`}
          onBlur={() => setBlurred(true)}
          onChange={(event) => {
            const next = formatNationalPhoneInput(event.target.value, country);
            setNational(next);
            emit(country, next);
          }}
        />
      </div>
      {showInvalid ? <p className="checkin-field-error">{t('guest.phoneInvalid')}</p> : null}
    </div>
  );
};

export { DEFAULT_COUNTRY };
